import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, takeUntil, tap } from 'rxjs/operators';
import {
  animate,
  animateChild,
  query,
  stagger,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { routeNames } from '@shared/models';
import { SharedService } from '@shared/services';
import { EpisodeMazcard, FormatEpisode, MazcardFile } from '@app/models';
import { ListState } from '@app/store/list.state';
import { PropertiesMazcardService } from './properties-mazcard.service';

@Component({
  selector: 'prp-properties-mazcard',
  templateUrl: './properties-mazcard.component.html',
  styleUrls: ['./properties-mazcard.component.scss'],
  animations: [
    // nice stagger effect when showing existing elements
    trigger('list', [
      transition(':enter', [
        // child animation selector + stagger
        query('@items', stagger(300, animateChild()), { optional: true }),
      ]),
    ]),
    trigger('items', [
      // cubic-bezier for a tiny bouncing feel
      transition(':enter', [
        style({ transform: 'scale(0.8)', opacity: 0 }),
        animate('0.8s cubic-bezier(.8,-0.6,0.2,1.5)', style({ transform: 'scale(1)', opacity: 1 })),
      ]),
      transition(':leave', [
        style({ transform: 'scale(1)', opacity: 1, height: '*' }),
        animate(
          '0.8s cubic-bezier(.8,-0.6,0.2,1.5)',
          style({
            transform: 'scale(0.5)',
            opacity: 0,
            height: '0px',
          })
        ),
      ]),
    ]),
  ],
})
export class PropertiesMazcardComponent implements OnDestroy, OnInit {
  data$: Observable<EpisodeMazcard[]>;
  routeNames = routeNames;
  private itemManuallySelected = false;

  private destroy$ = new Subject<void>();
  @Select(ListState.episode) loading$: Observable<any>;

  constructor(
    private store: Store,
    private sharedService: SharedService,
    private service: PropertiesMazcardService
  ) {}

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    // initiate from router parameters
    this.sharedService.routerParamsWatch
      .pipe(
        takeUntil(this.destroy$),
        filter((v: any) => !!v?.mazId && !!v?.fileNumber),
        tap((v: any) =>
          this.itemManuallySelected
            ? (this.itemManuallySelected = false)
            : this.select(v.mazId, v.fileNumber, false)
        )
      )
      .subscribe();
    // initiate data
    this.data$ = this.store.select(ListState.episode).pipe(
      takeUntil(this.destroy$),
      map((data: FormatEpisode) =>
        !data || !data.mazCards || !data.mazCards.length ? null : data.mazCards
      )
    );
  }

  select(mazNumber: string, file: string, navigate = true) {
    this.itemManuallySelected = true;
    this.service.select(mazNumber, file, navigate);
  }

  isSelected(mazNumber: string, file: string): Observable<boolean> {
    return this.store.select(ListState.fileSelected).pipe(
      map((data: MazcardFile) => {
        return !!data && data.mazNr === mazNumber && `${data.file}` === `${file}`;
      })
    );
  }

  displayNew(files: string[]) {
    return !files.some((i) => i === routeNames.newFileID);
  }

  fileName(name: string) {
    return name === routeNames.newFileID ? `properties.maz.${name}File` : name;
  }

  trackById(file) {
    return file;
  }
}
